import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { id } from './id';
import { en } from './en';

/**
 * Two languages, Indonesian first.
 *
 * Indonesian is the default and the source of truth: this is a warehouse app
 * used in Indonesia, and the English is a translation of it rather than the
 * other way round. `en.ts` is typed against `id.ts`, so a key added in
 * Indonesian and forgotten in English is a compile error, not a screen that
 * silently falls back to the wrong language.
 */

export type Locale = 'id' | 'en';
export type TranslationKey = keyof typeof id;

const DICTIONARIES: Record<Locale, Record<TranslationKey, string>> = { id, en };
const STORAGE_KEY = 'dr-inbound.locale';

/** Values substituted into a string's {placeholders}. */
export type Vars = Record<string, string | number>;

/**
 * Translate outside React — used by the fetch layer, which turns a server
 * error `code` into text before any component sees it, so every existing
 * `err.message` display site stays unchanged. Indonesian is the fallback for
 * an unknown key, matching the provider's `t`.
 */
export function translate(locale: Locale, key: TranslationKey | string, vars?: Vars): string {
  const table = DICTIONARIES[locale];
  const template = table[key as TranslationKey] ?? id[key as TranslationKey] ?? key;
  if (!vars) return template;
  return template.replace(/\{(\w+)\}/g, (match, name: string) =>
    name in vars ? String(vars[name]) : match,
  );
}

/** Whether a string is a key the dictionary actually knows. */
export function hasTranslation(key: string): key is TranslationKey {
  return key in id;
}

/**
 * Render a server-sent message in the current language.
 *
 * Success responses carry a `messageCode` the same way errors carry a `code`;
 * this turns it into text, falling back to the server's own (Indonesian)
 * `message` when the code is absent or unknown. The same shape as the fetch
 * layer's error handling, so both follow the ID|EN switch identically.
 */
export function localizeMessage(
  locale: Locale,
  code: string | undefined,
  fallback: string,
  vars?: Vars,
): string {
  if (!code) return fallback;
  const text = translate(locale, code, vars);
  return text === code ? fallback : text;
}

/** The stored locale, readable without the React context. Default Indonesian. */
export function readStoredLocale(): Locale {
  return readStored();
}

export interface LocaleContextValue {
  locale: Locale;
  setLocale: (next: Locale) => void;
  t: (key: TranslationKey, vars?: Vars) => string;
}

const LocaleContext = createContext<LocaleContextValue | null>(null);

function readStored(): Locale {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === 'id' || saved === 'en') return saved;
  } catch {
    // Private mode, or storage disabled. Fall through to the default.
  }
  return 'id';
}

export function LocaleProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(readStored);

  const setLocale = useCallback((next: Locale) => {
    setLocaleState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // The choice still applies to this session; it just will not persist.
    }
  }, []);

  // Screen readers and the browser's own spellchecker both key off this.
  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  const value = useMemo<LocaleContextValue>(
    () => ({
      locale,
      setLocale,
      t: (key, vars) => translate(locale, key, vars),
    }),
    [locale, setLocale],
  );

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useLocale(): LocaleContextValue {
  const ctx = useContext(LocaleContext);
  if (!ctx) throw new Error('useLocale must be used inside a LocaleProvider');
  return ctx;
}

/** The common case: just the translate function. */
export function useT(): LocaleContextValue['t'] {
  return useLocale().t;
}
